/**
 * Sector operations — Hospital Today Helpers (R39 C2.b, SO-P253).
 * Pure domain helpers for unit subtree resolution, current shift resolution,
 * shift-due matching, today due grouping, and unit exception inbox aggregation.
 */

import { zonedTimeToUtc } from '../../../charts/engine/bucket'
import {
  resolveWorkspaceTimeZone,
  workspaceTodayKey,
} from '../../../services/repeat/keys'
import {
  addDaysToKey,
  OPS_REQUEST_CLOSED_STATES,
  type OpsAsset,
  type OpsBedState,
  type OpsDue,
  type OpsPlace,
  type OpsRequest,
  type OpsSchedule,
  type OpsShift,
} from '../../../types/ops'

function getZonedMinuteEpoch(
  dayKey: string,
  minute: number,
  timeZone: string,
): number {
  const [y, m, d] = dayKey.split('-').map(Number)
  const hour = Math.floor(minute / 60)
  const min = minute % 60
  return zonedTimeToUtc(y, m, d, hour, min, 0, 0, timeZone).getTime()
}

/**
 * Returns a Set of place IDs representing the unit itself and all descendant places
 * in its subtree (wards, rooms, beds, etc.).
 */
export function unitSubtreeIds(
  places: readonly OpsPlace[],
  unitId: string,
): Set<string> {
  const result = new Set<string>()
  if (!unitId) return result
  result.add(unitId)

  let added = true
  while (added) {
    added = false
    for (const p of places) {
      if (!p.id || result.has(p.id)) continue
      if (
        (p.parentId && result.has(p.parentId)) ||
        p.path?.some((ancId) => result.has(ancId))
      ) {
        result.add(p.id)
        added = true
      }
    }
  }

  return result
}

/**
 * Resolves the active OpsShift covering `nowMs` in the given `timeZone`.
 * Handles daytime shifts as well as overnight shifts (where endMinute <= startMinute).
 */
export function currentShift(
  shifts: readonly OpsShift[],
  nowMs: number,
  timeZone: string,
): OpsShift | undefined {
  const tz = resolveWorkspaceTimeZone(timeZone)
  const todayKey = workspaceTodayKey('DAILY', tz, new Date(nowMs))
  const activeShifts = shifts.filter((s) => !s.archived)

  if (activeShifts.length === 0) return undefined

  // Check today first, then yesterday for overnight shifts that started yesterday
  const daysToCheck = [todayKey, addDaysToKey(todayKey, -1)]

  for (const dayKey of daysToCheck) {
    for (const shift of activeShifts) {
      const startMs = getZonedMinuteEpoch(dayKey, shift.startMinute, tz)
      const endMinute =
        typeof shift.endMinute === 'number'
          ? shift.endMinute
          : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
      const crossesMidnight = endMinute <= shift.startMinute

      if (dayKey !== todayKey && !crossesMidnight) {
        continue
      }

      const endDayKey = crossesMidnight ? addDaysToKey(dayKey, 1) : dayKey
      const endMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)

      if (nowMs >= startMs && nowMs < endMs) {
        return shift
      }
    }
  }

  return undefined
}

/**
 * Checks if an OpsDue belongs to a shift:
 * - If cadence is PER_SHIFT, matches by cadence.shiftId.
 * - Otherwise matches by windowStart falling inside the shift on that day.
 */
export function dueInShift(
  due: OpsDue,
  shift: OpsShift,
  schedulesById: Record<string, OpsSchedule> | Map<string, OpsSchedule>,
  timeZone: string,
): boolean {
  const schedule =
    schedulesById instanceof Map
      ? schedulesById.get(due.scheduleId)
      : schedulesById[due.scheduleId]

  if (schedule?.cadence?.kind === 'PER_SHIFT') {
    return schedule.cadence.shiftId === shift.id
  }

  const tz = resolveWorkspaceTimeZone(timeZone)
  const dueDayKey = workspaceTodayKey('DAILY', tz, new Date(due.windowStart))
  const endMinute =
    typeof shift.endMinute === 'number'
      ? shift.endMinute
      : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
  const crossesMidnight = endMinute <= shift.startMinute

  // Check if windowStart falls in the shift starting on dueDayKey
  const startMsToday = getZonedMinuteEpoch(dueDayKey, shift.startMinute, tz)
  const endDayKeyToday = crossesMidnight ? addDaysToKey(dueDayKey, 1) : dueDayKey
  const endMsToday = getZonedMinuteEpoch(endDayKeyToday, endMinute, tz)
  if (due.windowStart >= startMsToday && due.windowStart < endMsToday) {
    return true
  }

  // If overnight, also check if windowStart falls in the shift starting yesterday
  if (crossesMidnight) {
    const yesterdayKey = addDaysToKey(dueDayKey, -1)
    const startMsYesterday = getZonedMinuteEpoch(yesterdayKey, shift.startMinute, tz)
    const endMsYesterday = getZonedMinuteEpoch(dueDayKey, endMinute, tz)
    if (due.windowStart >= startMsYesterday && due.windowStart < endMsYesterday) {
      return true
    }
  }

  return false
}

export type TodayDueGroup =
  | 'MISSED_ESCALATED'
  | 'DUE_NOW'
  | 'UPCOMING'
  | 'DONE_SKIPPED'

export interface GroupedTodayDues {
  MISSED_ESCALATED: OpsDue[]
  DUE_NOW: OpsDue[]
  UPCOMING: OpsDue[]
  DONE_SKIPPED: OpsDue[]
}

/**
 * Groups today's dues into four distinct categories:
 * - MISSED_ESCALATED: status === 'MISSED' or (status === 'OPEN' and escalations > 0 or window+grace expired)
 * - DUE_NOW: status === 'OPEN', not escalated, now >= windowStart and not expired
 * - UPCOMING: status === 'OPEN', not escalated, now < windowStart
 * - DONE_SKIPPED: status === 'DONE' | 'LATE_DONE' | 'SKIPPED'
 */
export function groupTodayDues(
  dues: readonly OpsDue[],
  nowMs: number,
): GroupedTodayDues {
  const result: GroupedTodayDues = {
    MISSED_ESCALATED: [],
    DUE_NOW: [],
    UPCOMING: [],
    DONE_SKIPPED: [],
  }

  for (const due of dues) {
    if (
      due.status === 'DONE' ||
      due.status === 'LATE_DONE' ||
      due.status === 'SKIPPED'
    ) {
      result.DONE_SKIPPED.push(due)
      continue
    }

    const graceMs = (due.graceMinutes ?? 0) * 60_000
    const isPastGrace = due.windowEnd + graceMs < nowMs
    const hasEscalations = Boolean(due.escalations && due.escalations.length > 0)

    if (due.status === 'MISSED' || hasEscalations || isPastGrace) {
      result.MISSED_ESCALATED.push(due)
    } else if (nowMs >= due.windowStart) {
      result.DUE_NOW.push(due)
    } else {
      result.UPCOMING.push(due)
    }
  }

  return result
}

export interface UnitExceptionsOptions {
  dues: readonly OpsDue[]
  requests: readonly OpsRequest[]
  assets: readonly OpsAsset[]
  bedStates: readonly OpsBedState[]
  unitIds: ReadonlySet<string>
  todayKey?: string
}

export interface UnitExceptionsResult {
  missedEscalatedDues: OpsDue[]
  skippedDues: OpsDue[]
  openSpillRequests: OpsRequest[]
  openCriticalFaultRequests: OpsRequest[]
  blockedBeds: OpsBedState[]
}

/**
 * Collects unit exceptions for the in-charge inbox:
 * - missed/escalated dues in the unit
 * - today's skipped dues with reasons in the unit
 * - open SPILL requests in the unit
 * - open FAULT requests on HIGH/CRITICAL assets in the unit
 * - BLOCKED beds in the unit
 */
export function unitExceptions({
  dues,
  requests,
  assets,
  bedStates,
  unitIds,
  todayKey,
}: UnitExceptionsOptions): UnitExceptionsResult {
  const assetsById = new Map<string, OpsAsset>()
  for (const a of assets) {
    if (a.id) assetsById.set(a.id, a)
  }

  // 1. Dues in unit
  const missedEscalatedDues: OpsDue[] = []
  const skippedDues: OpsDue[] = []

  for (const due of dues) {
    if (!unitIds.has(due.placeId)) continue

    if (due.status === 'MISSED' || (due.escalations && due.escalations.length > 0)) {
      missedEscalatedDues.push(due)
    } else if (
      due.status === 'SKIPPED' &&
      Boolean(due.skipReason) &&
      (!todayKey || due.periodKey === todayKey)
    ) {
      skippedDues.push(due)
    }
  }

  // 2. Requests in unit
  const openSpillRequests: OpsRequest[] = []
  const openCriticalFaultRequests: OpsRequest[] = []

  for (const req of requests) {
    const isClosed = OPS_REQUEST_CLOSED_STATES.has(req.state)
    if (isClosed) continue

    const asset = req.assetId ? assetsById.get(req.assetId) : undefined
    const inUnit =
      unitIds.has(req.placeId) || (asset?.placeId && unitIds.has(asset.placeId))

    if (!inUnit) continue

    if (req.kind === 'SPILL') {
      openSpillRequests.push(req)
    } else if (req.kind === 'FAULT' || req.kind === 'EQUIPMENT_FAULT') {
      if (asset && (asset.criticality === 'HIGH' || asset.criticality === 'CRITICAL')) {
        openCriticalFaultRequests.push(req)
      }
    }
  }

  // 3. Beds in unit
  const blockedBeds: OpsBedState[] = []
  for (const bed of bedStates) {
    if (bed.state !== 'BLOCKED') continue

    const inUnit =
      unitIds.has(bed.placeId) ||
      (bed.wardPlaceId ? unitIds.has(bed.wardPlaceId) : false) ||
      (bed.roomPlaceId ? unitIds.has(bed.roomPlaceId) : false)

    if (inUnit) {
      blockedBeds.push(bed)
    }
  }

  return {
    missedEscalatedDues,
    skippedDues,
    openSpillRequests,
    openCriticalFaultRequests,
    blockedBeds,
  }
}
